import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { LegacyFilesService } from '../../core/services/legacy-files.service';

/**
 * Handles the old site's file URLs (/wp-content/uploads/...).
 *
 * Those links have been shared by email for years, so after the cutover they
 * must still reach the file rather than bouncing to the homepage. The path is
 * resolved to a presigned S3 URL and the download starts on its own.
 */
@Component({
    selector: 'app-legacy-file',
    imports: [MenuBarComponent, FooterComponent],
    templateUrl: './legacy-file.component.html',
    styleUrl: './legacy-file.component.scss',
})
export class LegacyFileComponent implements OnInit {
    state: 'resolving' | 'started' | 'not-found' | 'error' = 'resolving';
    filename = '';

    constructor(
        private legacyFiles: LegacyFilesService,
        private router: Router,
    ) { }

    ngOnInit(): void {
        // router.url is the whole path as typed, which is what the backend
        // matches against the stored legacyUrl.
        const path = this.router.url.split('?')[0].split('#')[0];

        // Where the backend redirect sends people when nothing claims the path.
        // There is nothing to resolve here — just say so.
        if (path.startsWith('/file-unavailable')) {
            this.state = 'not-found';
            return;
        }

        this.legacyFiles.resolve(path).subscribe({
            next: (res) => {
                if (!res?.url) {
                    this.state = 'not-found';
                    return;
                }
                this.filename = res.filename || '';
                this.state = 'started';
                this.startDownload(res.url);
            },
            // The backend answers 404 for both "no such file" and "no object",
            // which is the same thing from the visitor's point of view. The status
            // is nested because handleError rethrows { httpError, service }.
            error: (err) => {
                const status = err?.httpError?.status ?? err?.status;
                this.state = status === 404 ? 'not-found' : 'error';
            },
        });
    }

    public goHome(): void {
        this.router.navigate(['/']);
    }

    /**
     * The URL carries Content-Disposition: attachment, so this saves the file
     * without navigating away from the page.
     */
    private startDownload(url: string): void {
        const link = document.createElement('a');
        link.href = url;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
    }
}
